import type { QuestionnaireDTOType } from '@/types/portal-api.generated'

export type EligibilityQuestionnairePosition =
  'after_kit_id' | 'after_consent' | 'after_personal_info' | 'after_address_info'

const ELIGIBILITY_QUESTIONNAIRE_POSITIONS = new Set<string>([
  'after_kit_id',
  'after_consent',
  'after_personal_info',
  'after_address_info',
])

export function readEligibilityQuestionnairePositionFromQuestionnaire(
  questionnaire: QuestionnaireDTOType | null,
): EligibilityQuestionnairePosition {
  const position = questionnaire?.pages[0]?.position
  if (position && ELIGIBILITY_QUESTIONNAIRE_POSITIONS.has(position)) {
    return position as EligibilityQuestionnairePosition
  }

  return 'after_kit_id'
}

/**
 * Patient Portal only. Patient Portal submits the registration at the Address step,
 * so when the PCP step is disabled, `after_address_info` moves to `after_personal_info`.
 * Do not use for Client Portal: it submits at Review, so this change is incorrect there.
 */
export function normalizePatientEligibilityQuestionnairePosition(
  position: EligibilityQuestionnairePosition,
  options: { isPcpStepEnabled?: boolean },
): EligibilityQuestionnairePosition {
  if (options.isPcpStepEnabled !== true && position === 'after_address_info') {
    return 'after_personal_info'
  }

  return position
}

export function getEligibilityQuestionnairePosition(
  questionnaire: QuestionnaireDTOType | null,
  options: { isPcpStepEnabled?: boolean },
): EligibilityQuestionnairePosition {
  const position = readEligibilityQuestionnairePositionFromQuestionnaire(questionnaire)
  return normalizePatientEligibilityQuestionnairePosition(position, options)
}

export function insertStepAfterAnchor<T extends { key: string }>(
  steps: readonly T[],
  options: {
    position: EligibilityQuestionnairePosition
    positionToAnchor: Record<EligibilityQuestionnairePosition, string>
    stepToInsert: T
  },
): T[] {
  const stepsWithoutInserted = steps.filter((step) => step.key !== options.stepToInsert.key)
  const anchorKey = options.positionToAnchor[options.position]
  const anchorIndex = stepsWithoutInserted.findIndex((step) => step.key === anchorKey)
  const insertAt = anchorIndex === -1 ? 0 : anchorIndex + 1

  return [
    ...stepsWithoutInserted.slice(0, insertAt),
    options.stepToInsert,
    ...stepsWithoutInserted.slice(insertAt),
  ]
}
